import axios from "axios";
import { ApiResponse, RegisterData, User } from "../types";
import { decryptData, encryptData } from '../utils/encryption';

// Vite uses import.meta.env for env variables. Provide sensible default to '/api'.
const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || "/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect if we're already on the login page or reset password pages (to prevent refresh loop)
      const currentPath = window.location.pathname;
      const isPublicRoute = 
        currentPath === '/login' || 
        currentPath.startsWith('/login') ||
        currentPath.startsWith('/forget-password/') ||
        currentPath.startsWith('/forgot-password/') ||
        currentPath === '/forgot-password' ||
        currentPath === '/register';
      
      if (!isPublicRoute) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (
    payload: any
  ): Promise<
    ApiResponse<{ user: User; token: string; requiresMFA?: boolean }>
  > => {
    const response = await api.post("/auth/login", {request:payload});
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  // New email-only TOTP initiation
  startEmailLogin: async (
    email: string
  ): Promise<
    ApiResponse<{ requiresTotp: boolean; alreadySetup: boolean; qrCode?: string }>
  > => {
    const request = encryptData({ email });
    const response = await api.post("/auth/login-email", { request });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  // New TOTP verification endpoint
  verifyTotp: async (
    email: string,
    token: string
  ): Promise<
    ApiResponse<{ user: User; token: string }>
  > => {
    const request = encryptData({ email, token });
    const response = await api.post("/auth/verify-totp", { request });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  // Admin/Support: reset or generate QR for a user by email
  resetMfaQr: async (
    email: string
  ): Promise<
    ApiResponse<{ qrCode: string }>
  > => {
    const response = await api.post("/auth/reset-qr", { email });
    return response.data;
  },

  register: async (
    userData: RegisterData
  ): Promise<ApiResponse<{ user: User; token: string }>> => {
    const response = await api.post("/auth/register", userData);
    return response.data;
  },

  logout: async () => {
    const response = await api.post("/auth/logout");
    return response.data;
  },

  verify: async (): Promise<ApiResponse<{ user: User }>> => {
    const response = await api.get("/auth/verify");
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  requestOTP: async (email: string): Promise<ApiResponse<{ message: string }>> => {
    const request = encryptData({ email });
    const response = await api.post("/auth/request-otp", { request });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  verifyOTP: async (email: string, otp: string): Promise<ApiResponse<{ token: string }>> => {
    const request = encryptData({ email, otp });
    const response = await api.post("/auth/verify-otp", { request });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  resetPassword: async (email: string, newPassword: string): Promise<ApiResponse<{ message: string }>> => {
    const request = encryptData({ email, newPassword });
    const response = await api.post("/auth/reset-password", { request });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },
  forgetPassword: async (email: string, newPassword: string): Promise<ApiResponse<{ message: string }>> => {
    const request = encryptData({ email, newPassword });
    const response = await api.post("/auth/forget-password", { request });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  checkPasswordReset: async (request: string): Promise<ApiResponse<{ email: string; password_reset: boolean }>> => {
    const response = await api.post("/users/check-password-reset", { request: request });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },
};

// Dashboard API
export const dashboardAPI = {
  getStats: async () => {
    const response = await api.get("/dashboard/stats");
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  getVendorPerformance: async () => {
    const response = await api.get("/dashboard/vendor-performance");
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  getVendorAnalytics: async (params?: Record<string, any>) => {
    const response = await api.get("/dashboard/vendor-analytics", { params });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  getVendorAnalyticsDaily: async (params: { date: string } & Record<string, any>) => {
    const response = await api.get("/dashboard/vendor-analytics/daily", { params });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },
};

// Companies API
export const companiesAPI = {
  getAll: async () => {
    const response = await api.get("/companies");
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/companies/${id}`);
    return response.data;
  },

  create: async (companyData: any) => {
    const response = await api.post("/companies", companyData);
    return response.data;
  },

  update: async (id: number, companyData: any) => {
    const response = await api.put(`/companies/${id}`, companyData);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/companies/${id}`);
    return response.data;
  },
};

// Vendors API
export const vendorsAPI = {
  getAll: async (options?: {
    page?: number;
    limit?: number;
    search?: string;
    order?: "asc" | "desc";
  }) => {
    const params: any = {};
    
    if (options) {
      if (options.page !== undefined) params.page = options.page;
      if (options.limit !== undefined) params.limit = options.limit;
      if (options.search && options.search.trim()) params.search = options.search.trim();
      if (options.order) params.order = options.order;
    }
    
    const response = await api.get("/vendors", { params });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  getById: async (id: number) => {
    const response = await api.get(`/vendors/${id}`);
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  create: async (vendorData: any) => {
    const request = encryptData(vendorData);
    const response = await api.post("/vendors", {request});
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  update: async (id: number, vendorData: any) => {
    const request = encryptData(vendorData);
    const response = await api.put(`/vendors/${id}`, {request});
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/vendors/${id}`);
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  bulkImport: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/vendors/bulk-import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  dropdown: async (id: any) => {
    const response = await api.get('/vendors/by-department', {
      params: { department_id: id },
    });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },
  allVendorsDropdown: async () => {
    const response = await api.get('/vendors/dropdown');
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },  
};

// Vendor Invoices API
export const vendorInvoicesAPI = {
  getAll: async (params?: { page?: number; limit?: number; search?: string; status?: string }) => {
    // Clean params: remove undefined to avoid sending empty query keys
    const cleanParams: any = {};
    if (params) {
      if (params.page !== undefined) cleanParams.page = params.page;
      if (params.limit !== undefined) cleanParams.limit = params.limit;
      if (params.search !== undefined && params.search !== '') cleanParams.search = params.search;
      if (params.status !== undefined && params.status !== '' && params.status !== 'all') cleanParams.status = params.status;
    }
    const response = await api.get("/vendor-invoices", { params: cleanParams });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  getById: async (id: number) => {
    const response = await api.get(`/vendor-invoices/${id}`);
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  upload: async (formData: FormData) => {
    const response = await api.post("/vendor-invoices", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  update: async (id: number, formData: FormData) => {
    const response = await api.put(`/vendor-invoices/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  updateStatus: async (id: number, status?: string, notes?: string, bankId?: string, financeStatus?: string, financeAccountNo?: string, financeAccountName?: string) => {
    const response = await api.put(`/vendor-invoices/${id}/status`, {
      status,
      notes,
      bank_id: bankId,
      finance_status: financeStatus,
      finance_account_no: financeAccountNo,
      finance_account_name: financeAccountName,
    });
    return response.data;
  },

  markPaid: async (
    id: number,
    paidAmount: number,
    paymentDate?: string,
    notes?: string,
    referenceNumber?: string
  ) => {
    const response = await api.post(`/vendor-invoices/${id}/mark-paid`, {
      paid_amount: paidAmount,
      payment_date: paymentDate,
      notes,
      reference_number: referenceNumber,
    });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  // Mark paid with file attachment (multipart)
  markPaidWithAttachment: async (
    id: number,
    params: {
      paid_amount: number;
      payment_date?: string;
      notes?: string;
      reference_number?: string;
      payment_attachement: File;
    }
  ) => {
    const formData = new FormData();
    formData.append('paid_amount', String(params.paid_amount));
    if (params.payment_date) formData.append('payment_date', params.payment_date);
    if (params.notes) formData.append('notes', params.notes);
    if (params.reference_number) formData.append('reference_number', params.reference_number);
    // Required field name per spec
    formData.append('payment_attachement', params.payment_attachement);

    const response = await api.post(`/vendor-invoices/${id}/mark-paid`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  download: async (id: number) => {
    const response = await api.get(`/vendor-invoices/${id}/download`, {
      responseType: "blob",
    });
    return response;
  },

  downloadAdditionalAttachment: async (invoiceId: number, filename: string) => {
    const response = await api.get(`/vendor-invoices/${invoiceId}/attachments/${encodeURIComponent(filename)}/download`, {
      responseType: 'blob',
    });
    return response;
  },

  // New API per spec: DELETE /api/vendor-invoices/:id/additional-attachments?filepath=...
  deleteAdditionalAttachment: async (invoiceId: number, filepath: string) => {
    const response = await api.delete(`/vendor-invoices/${invoiceId}/additional-attachments`, {
      params: { filepath }
    });
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/vendor-invoices/${id}`);
    return response.data;
  },

  invoiceApproverDropdown: async () => {
    const response = await api.get("/users/invoice-approvers");
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  paymentApproverDropdown: async () => {
    const response = await api.get("/users/payment-approvers");
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  getAuditTrail: async (id: number) => {
    const response = await api.get(`/vendor-invoices/${id}/audit-trail`);
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  getVendorMonthlyStats: async (vendorId: number) => {
    const response = await api.get(`/vendor-invoices/vendor/${vendorId}/monthly-stats`);
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  // Approver status update per-level (1..3)
  setApproverStatus: async (
    id: number,
    level: 1 | 2 | 3,
    status: "approved" | "rejected",
    comments?: string
  ) => {
    const response = await api.post(`/vendor-invoices/${id}/approver-status`, {
      level,
      status,
      comments,
    });
    return response.data;
  },

  // Stats for vendor invoices
  getStats: async () => {
    const response = await api.get('/vendor-invoices/stats');
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  // Check if invoice number is duplicate for a vendor
  checkDuplicateInvoice: async (vendorId: number, invoiceNumber: string) => {
    const response = await api.get('/vendor-invoices/check-duplicate', {
      params: { vendor_id: vendorId, invoice_number: invoiceNumber }
    });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  // Check if registration number matches vendor's registered number
  checkRegistrationNumber: async (vendorId: number, registrationNumber: string) => {
    const response = await api.get('/vendor-invoices/check-registration', {
      params: { vendor_id: vendorId, registration_number: registrationNumber }
    });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  // Approve or reject vendor invoice (dynamic approvers flow)
  approve: async (id: number, action: "approved" | "rejected", notes?: string) => {
    const payload: any = {
      action,
    };
    if (notes) payload.notes = notes;
    
    const response = await api.post(`/vendor-invoices/${id}/approve`, payload);
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

};

// Invoices API
export const invoicesAPI = {
  getAll: async () => {
    const response = await api.get("/invoices");
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/invoices/${id}`);
    return response.data;
  },

  create: async (invoiceData: any) => {
    const response = await api.post("/invoices", invoiceData);
    return response.data;
  },

  update: async (id: number, invoiceData: any) => {
    const response = await api.put(`/invoices/${id}`, invoiceData);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/invoices/${id}`);
    return response.data;
  },
};

// Estimates API
export const estimatesAPI = {
  getAll: async () => {
    const response = await api.get("/estimates");
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/estimates/${id}`);
    return response.data;
  },

  create: async (estimateData: any) => {
    const response = await api.post("/estimates", estimateData);
    return response.data;
  },

  update: async (id: number, estimateData: any) => {
    const response = await api.put(`/estimates/${id}`, estimateData);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/estimates/${id}`);
    return response.data;
  },

  send: async (id: number) => {
    const response = await api.put(`/estimates/${id}`, { status: "sent" });
    return response.data;
  },
};

// Bills API
export const billsAPI = {
  getAll: async () => {
    const response = await api.get("/bills");
    return response.data;
  },

  create: async (billData: any) => {
    const response = await api.post("/bills", billData);
    return response.data;
  },

  update: async (id: number, billData: any) => {
    const response = await api.put(`/bills/${id}`, billData);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/bills/${id}`);
    return response.data;
  },
};

// Time Entries API
export const timeEntriesAPI = {
  getAll: async () => {
    const response = await api.get("/time-entries");
    return response.data;
  },

  create: async (timeEntryData: any) => {
    const response = await api.post("/time-entries", timeEntryData);
    return response.data;
  },

  update: async (id: number, timeEntryData: any) => {
    const response = await api.put(`/time-entries/${id}`, timeEntryData);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/time-entries/${id}`);
    return response.data;
  },
};

// Bank Reconciliation API
export const bankReconciliationAPI = {
  getStatements: async () => {
    const response = await api.get("/reconciliation/statements");
    return response.data;
  },

  uploadStatement: async (formData: FormData) => {
    const response = await api.post(
      "/reconciliation/statements/upload",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  getTransactions: async (statementId: number) => {
    const response = await api.get(
      `/reconciliation/statements/${statementId}/transactions`
    );
    return response.data;
  },

  generateReport: async (statementId: number) => {
    const response = await api.get(
      `/reconciliation/statements/${statementId}/report`
    );
    return response.data;
  },

  createManualMatch: async (
    bankTransactionId: number,
    invoiceId: number,
    notes?: string
  ) => {
    const response = await api.post("/reconciliation/match", {
      bank_transaction_id: bankTransactionId,
      invoice_id: invoiceId,
      notes,
    });
    return response.data;
  },

  removeMatch: async (transactionId: number) => {
    const response = await api.delete(`/reconciliation/match/${transactionId}`);
    return response.data;
  },

  runAutoMatch: async (statementId: number) => {
    const response = await api.post(
      `/reconciliation/statements/${statementId}/auto-match`
    );
    return response.data;
  },

  getDashboard: async () => {
    const response = await api.get("/reconciliation/dashboard");
    return response.data;
  },
};

// Vendor Onboarding API
export const vendorOnboardingAPI = {
  getTemplates: async () => {
    const response = await api.get("/vendor-onboarding/templates");
    return response.data;
  },

  getVendorForms: async (vendorId: number) => {
    const response = await api.get(`/vendor-onboarding/vendor/${vendorId}`);
    return response.data;
  },

  createForm: async (formData: {
    vendor_id: number;
    form_type: string;
    form_data: any;
    status?: string;
  }) => {
    const response = await api.post("/vendor-onboarding/forms", formData);
    return response.data;
  },

  submitForm: async (formId: number) => {
    const response = await api.post(
      `/vendor-onboarding/forms/${formId}/submit`
    );
    return response.data;
  },

  reviewForm: async (
    formId: number,
    action: "approve" | "reject",
    comments?: string
  ) => {
    const response = await api.post(
      `/vendor-onboarding/forms/${formId}/review`,
      {
        action,
        comments,
      }
    );
    return response.data;
  },

  downloadPDF: async (formId: number) => {
    const response = await api.get(`/vendor-onboarding/forms/${formId}/pdf`, {
      responseType: "blob",
    });
    return response;
  },

  getWorkflowStatus: async (vendorId: number) => {
    const response = await api.get(`/vendor-onboarding/workflow/${vendorId}`);
    return response.data;
  },
};

// Workflow Management API
export const workflowAPI = {
  getRules: async (entityType?: string) => {
    const params = entityType ? { entity_type: entityType } : {};
    const response = await api.get("/workflows/rules", { params });
    return response.data;
  },

  createRule: async (ruleData: {
    name: string;
    entity_type: string;
    trigger_conditions: any;
    approval_levels: any[];
    is_active?: boolean;
  }) => {
    const response = await api.post("/workflows/rules", ruleData);
    return response.data;
  },

  getPendingApprovals: async () => {
    const response = await api.get("/workflows/pending");
    return response.data;
  },

  processApproval: async (
    approvalId: number,
    status: "approved" | "rejected",
    comments?: string
  ) => {
    const response = await api.post(
      `/workflows/approvals/${approvalId}/process`,
      {
        status,
        comments,
      }
    );
    return response.data;
  },

  getWorkflowHistory: async (entityType: string, entityId: number) => {
    const response = await api.get(
      `/workflows/history/${entityType}/${entityId}`
    );
    return response.data;
  },

  cancelWorkflow: async (instanceId: number) => {
    const response = await api.post(
      `/workflows/instances/${instanceId}/cancel`
    );
    return response.data;
  },
};

// MFA API
export const mfaAPI = {
  setup: () => api.post("/mfa/setup"),
  verify: (data: { token?: string; backupCode?: string }) =>
    api.post("/mfa/verify", data),
  disable: (data: { password: string }) => api.post("/mfa/disable", data),
  regenerateBackupCodes: (data: { password: string }) =>
    api.post("/mfa/backup-codes/regenerate", data),
  getStatus: () => api.get("/mfa/status"),
  getSessions: () => api.get("/mfa/sessions"),
  invalidateSession: (sessionId: string) =>
    api.delete(`/mfa/sessions/${sessionId}`),
  validateSession: (sessionToken: string) =>
    api.post("/mfa/validate-session", { sessionToken }),
};

// VAT API
export const vatAPI = {
  calculate: (data: any) => api.post("/vat/calculate", data),
  validateVAT: (data: { vatNumber: string }) =>
    api.post("/vat/validate-vat", data),
  generateReturn: (data: { periodStart: string; periodEnd: string }) =>
    api.post("/vat/return", data),
  getTransactions: (params: { periodStart: string; periodEnd: string }) =>
    api.get("/vat/transactions", { params }),
  exportReturn: (periodStart: string, periodEnd: string) =>
    api.get(`/vat/export/${periodStart}/${periodEnd}`),
  getRates: () => api.get("/vat/rates"),
  addRate: (data: any) => api.post("/vat/rates", data),
  updateRate: (id: string, data: any) => api.put(`/vat/rates/${id}`, data),
  getSummary: (params: { periodStart: string; periodEnd: string }) =>
    api.get("/vat/summary", { params }),
  recordTransaction: (data: any) => api.post("/vat/transactions", data),
};

// Compliance API
export const complianceAPI = {
  generateVATReturn: (data: { periodStart: string; periodEnd: string }) =>
    api.post("/compliance/vat-return", data),

  generateVendorStatement: (data: {
    vendorId: number;
    periodStart: string;
    periodEnd: string;
  }) => api.post("/compliance/vendor-statement", data),

  generateAuditTrail: (data: {
    periodStart: string;
    periodEnd: string;
    entityType?: string;
  }) => api.post("/compliance/audit-trail", data),

  getReports: (reportType?: string) =>
    api.get("/compliance/reports", { params: { reportType } }),

  getAuditLogs: (params: { periodStart?: string; periodEnd?: string }) =>
    api.get("/compliance/audit-logs", { params }),

  exportReport: (reportId: number, format: string = "pdf") =>
    api.get(`/compliance/reports/${reportId}/export`, { params: { format } }),

  submitVATReturn: (reportId: number) =>
    api.post(`/compliance/vat-return/${reportId}/submit`),
};

// User Management API
export const usersAPI = {
  getPermissions: async () => {
    const response = await api.get("/users/permissions");
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  getRoles: async () => {
    const response = await api.get("/users/roles");
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  getAll: async (options?: {
    page?: number;
    limit?: number;
    search?: string;
  }) => {
    const params = options
      ? {
        page: options.page,
        limit: options.limit,
        search: options.search,
      }
      : {};
    const response = await api.get("/users", { params });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  getById: async (id: number) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  create: async (userData: any) => {
    const request = encryptData(userData);
    const response = await api.post("/users", {request});
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  update: async (id: number, userData: any) => {
    const request = encryptData(userData);
    const response = await api.put(`/users/${id}`, {request});
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/users/${id}`);
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  updateRole: async (id: number, role: string) => {
    const response = await api.put(`/users/${id}/role`, { role });
    return response.data;
  },
  
  // Bulk upload users via Excel/CSV
  bulkUpload: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/users/bulk-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Check if employee ID exists
  checkEmployeeId: async (employeeId: string): Promise<ApiResponse<{ employee_id: string; exists: boolean }>> => {
    const request = encryptData({ employee_id: employeeId });
    const response = await api.post("/users/check-employee-id", { request });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },
};

// Departments API
export const departmentsAPI = {
  getAll: async (options?: {
    page?: number;
    limit?: number;
    search?: string;
  }) => {
    const params = options
      ? {
        page: options.page,
        limit: options.limit,
        search: options.search,
      }
      : {};
    const response = await api.get("/departments", { params });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  getById: async (id: number) => {
    const response = await api.get(`/departments/${id}`);
    return response.data;
  },

  create: async (departmentData: any) => {
    const request = encryptData(departmentData);
    const response = await api.post("/departments", {request});
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  update: async (id: number, departmentData: any) => {
    const request = encryptData(departmentData);
    const response = await api.put(`/departments/${id}`, {request});
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/departments/${id}`);
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  dropDown: async () => {
    const response = await api.get("/departments/dropdown");
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },
};

// Currencies API
export const currenciesAPI = {
  getAll: async (options?: {
    page?: number;
    limit?: number;
    search?: string;
  }) => {
    const params: Record<string, unknown> = {};
    if (options) {
      if (options.page !== undefined) {
        params.page = options.page;
      }
      if (options.limit !== undefined) {
        params.limit = options.limit;
      }
      if (options.search !== undefined && options.search !== "") {
        params.search = options.search;
      }
    }
    const response = await api.get("/currencies", { params });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  getById: async (id: number) => {
    const response = await api.get(`/currencies/${id}`);
    return response.data;
  },

  create: async (currencyData: any) => {
    const request = encryptData(currencyData);
    const response = await api.post("/currencies", { request });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  update: async (id: number, currencyData: any) => {
    const request = encryptData(currencyData);
    const response = await api.put(`/currencies/${id}`, { request });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/currencies/${id}`);
    return response.data;
  },

  dropDown: async () => {
    const response = await api.get("/currencies/dropdown");
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },
};

// bank API
export const bankAPI = {
  create: async (bankData: any) => {
    const response = await api.post("/banks", bankData);
    return response.data;
  },
  getAll: async (vendor_id: number) => {
    const response = await api.get(`/banks/${vendor_id}`);
    return response.data;
  },
};

// Financial Powers API
export const financialPowersAPI = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    department_id?: number;
    vendor_id?: number;
  }) => {
    const cleanParams: any = {};
    if (params) {
      if (params.page !== undefined) cleanParams.page = params.page;
      if (params.limit !== undefined) cleanParams.limit = params.limit;
      if (params.search !== undefined && params.search !== '') cleanParams.search = params.search;
      if (params.status !== undefined && params.status !== '' && params.status !== 'all') cleanParams.status = params.status;
      if (params.department_id !== undefined) cleanParams.department_id = params.department_id;
      if (params.vendor_id !== undefined) cleanParams.vendor_id = params.vendor_id;
    }
    const response = await api.get("/financial-powers", { params: cleanParams });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  getById: async (id: number) => {
    const response = await api.get(`/financial-powers/${id}`);
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  create: async (formData: FormData) => {
    const response = await api.post("/financial-powers", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  update: async (id: number, formData: FormData) => {
    const response = await api.put(`/financial-powers/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  approve: async (id: number, status: "approved" | "rejected", notes?: string, reference_name?: string) => {
    const payload: any = {
      status,
    };
    if (notes) payload.notes = notes;
    if (reference_name) payload.reference_name = reference_name;
    
    const response = await api.post(`/financial-powers/${id}/approve`, payload);
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/financial-powers/${id}`);
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  getAuditTrail: async (id: number) => {
    const response = await api.get(`/financial-powers/${id}/audit-trail`);
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  getByReference: async (referenceNumber: string) => {
    const response = await api.get(`/financial-powers/by-reference/${referenceNumber}`);
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  dropdown: async () => {
    const response = await api.get("/financial-powers/dropdown");
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },
};

// Financial Power Invoices API
export const financialPowerInvoicesAPI = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    reference_number?: string;
  }) => {
    const cleanParams: any = {};
    if (params) {
      if (params.page !== undefined) cleanParams.page = params.page;
      if (params.limit !== undefined) cleanParams.limit = params.limit;
      if (params.search !== undefined && params.search !== '') cleanParams.search = params.search;
      if (params.status !== undefined && params.status !== '' && params.status !== 'all') cleanParams.status = params.status;
      if (params.reference_number !== undefined && params.reference_number !== '') cleanParams.reference_number = params.reference_number;
    }
    const response = await api.get("/financial-power-invoices", { params: cleanParams });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  getById: async (id: number) => {
    const response = await api.get(`/financial-power-invoices/${id}`);
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  create: async (formData: FormData) => {
    const response = await api.post("/financial-power-invoices", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  update: async (id: number, formData: FormData) => {
    const response = await api.put(`/financial-power-invoices/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  approve: async (id: number, status: "approved" | "rejected", notes?: string) => {
    const payload: any = {
      status,
    };
    if (notes) payload.notes = notes;
    
    const response = await api.post(`/financial-power-invoices/${id}/approve`, payload);
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/financial-power-invoices/${id}`);
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  markPaid: async (
    id: number,
    paidAmount: number,
    paymentDate?: string,
    notes?: string,
    referenceNumber?: string
  ) => {
    const response = await api.post(`/financial-power-invoices/${id}/mark-paid`, {
      paid_amount: paidAmount,
      payment_date: paymentDate,
      notes,
      reference_number: referenceNumber,
    });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  // Mark paid with file attachment (multipart)
  markPaidWithAttachment: async (
    id: number,
    params: {
      paid_amount: number;
      payment_date?: string;
      notes?: string;
      reference_number?: string;
      payment_attachement: File;
    }
  ) => {
    const formData = new FormData();
    formData.append('paid_amount', String(params.paid_amount));
    if (params.payment_date) formData.append('payment_date', params.payment_date);
    if (params.notes) formData.append('notes', params.notes);
    if (params.reference_number) formData.append('reference_number', params.reference_number);
    // Required field name per spec
    formData.append('payment_attachement', params.payment_attachement);

    const response = await api.post(`/financial-power-invoices/${id}/mark-paid`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  getAuditTrail: async (id: number) => {
    const response = await api.get(`/financial-power-invoices/${id}/audit-trail`);
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },
};

// Reports API
export const reportsAPI = {
  getVendorInvoicesDetailed: async (params?: {
    vendor_id?: number;
    status?: string;
    start_date?: string;
    end_date?: string;
    department_id?: number;
    amount?: number;
    page?: number;
    limit?: number;
  }) => {
    const cleanParams: any = {};
    if (params) {
      if (params.vendor_id !== undefined) cleanParams.vendor_id = params.vendor_id;
      if (params.status !== undefined && params.status !== '') cleanParams.status = params.status;
      if (params.start_date !== undefined && params.start_date !== '') cleanParams.start_date = params.start_date;
      if (params.end_date !== undefined && params.end_date !== '') cleanParams.end_date = params.end_date;
      if (params.department_id !== undefined && params.department_id > 0) cleanParams.department_id = params.department_id;
      if (params.amount !== undefined && params.amount > 0) cleanParams.amount = params.amount;
      if (params.page !== undefined) cleanParams.page = params.page;
      if (params.limit !== undefined) cleanParams.limit = params.limit;
    }
    const response = await api.get("/reports/vendor-invoices/detailed", { params: cleanParams });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  getVendorInvoiceDetailedById: async (id: number) => {
    const response = await api.get(`/reports/vendor-invoices/${id}/detailed`);
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },

  exportVendorInvoices: async (params?: {
    vendor_id?: number;
    status?: string;
    start_date?: string;
    end_date?: string;
    department_id?: number;
    amount?: number;
  }) => {
    const cleanParams: any = {};
    if (params) {
      if (params.vendor_id !== undefined) cleanParams.vendor_id = params.vendor_id;
      if (params.status !== undefined && params.status !== '' && params.status !== 'all') cleanParams.status = params.status;
      if (params.start_date !== undefined && params.start_date !== '') cleanParams.start_date = params.start_date;
      if (params.end_date !== undefined && params.end_date !== '') cleanParams.end_date = params.end_date;
      if (params.department_id !== undefined && params.department_id > 0) cleanParams.department_id = params.department_id;
      if (params.amount !== undefined && params.amount > 0) cleanParams.amount = params.amount;
    }
    const response = await api.get("/reports/vendor-invoices/export", { params: cleanParams });
    const decryptedResponse = decryptData(response.data.data);
    return decryptedResponse;
  },
};

export default api;
